import React, { useEffect, useMemo, useRef, useState } from 'react';
import { sortable } from 'tea-component/lib/table/addons';
import BarChart from '../../../components/BarChart';
import LineChart from '../../../components/LineChart';
import PieChart from '../../../components/PieChart';
import { metricsMap } from '../../../constants';
import { dealwithChannelShareChartData } from '../../../utils';
import ChannelsShareTable from './ChannelsShareTable';
import {
  getStreamingChannelShare,
  getStreamingChannelShareTrends,
} from '@/pages/opinion/services/community';
import {
  arrayToSimpleObj,
  getRationPercentValue,
  handleMentionData,
  supplementSingleData,
  uniqueArray,
} from '@/pages/opinion/utils';
import { formatDateFunc } from '@/pages/opinion/utils/time';
import CardCommon from '@/pages/opinion/components/CardCommon';
import './index.less';
import { useIntl } from 'umi';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { reviewsFormat, scoreFormat } from '@/pages/opinion/utils/value';
import { barIcon, lineIcon, pieIcon } from '@/pages/opinion/constants/chart';
import { NO_DATE } from '@/pages/opinion/constants';
import BubbleCommon, {
  influencerCommonTipsText,
} from '@op/components/BubbleCommon';

export default function ChannelsShareInfo({
  metric,
  params,
  gameInfo,
  gameName,
  showTitleTips,
}: any) {
  const formatMetricValue = useMemo(() => {
    const formatMetricValueMap: any = {
      hoursWatched: (value: number) => scoreFormat(value, 2),
      peakCCV: (value: number) => reviewsFormat(value),
      avgCCV: (value: number) => scoreFormat(value, 2),
      airtime: (value: number) => scoreFormat(value, 2),
    };

    return formatMetricValueMap[metric];
  }, [metric]);

  const { formatMessage } = useIntl();
  const { downloadLegacy, downloading } = useDownloadParams();
  // 表格相关数据
  const [total, setTotal] = useState(0);
  const [tableRecords, setTableRecords] = useState<Array<any>>([]);

  // total列数据
  const [totalColumn, setTotalColumn] = useState({
    user_name: 'Total',
    value: 0,
    ration_percent: 0,
  });

  const [pieAndBarRecords, setPieAndBarRecords] = useState<Array<any>>([]);
  const [lineSeriesData, setLineSeriesData] = useState<Array<any>>([]);
  const [lineXAxisData, setLineXAxisData] = useState<Array<any>>([]);

  const [sort, setSort] = useState<{ by: string; order: 'asc' | 'desc' }>({
    by: 'value',
    order: 'desc',
  });
  const [type, setType] = useState<'pie' | 'line' | 'bar'>('pie');
  const [loading, setLoading] = useState(false);
  const [lineLoading, setLineLoading] = useState(false);

  const pieAndBarRecordsMap = useRef({});

  const clearRecords = () => {
    setPieAndBarRecords([]);
    setTableRecords([]);
    setLineXAxisData([]);

    setTotal(0);
  };

  const getServerDataInfo = () => {
    if (Object.keys(params).length === 0 || !gameInfo.edition_unified_id) {
      return;
    }
    let paramsInfo = {
      ...params,
      ...gameInfo,
      metric: metric,
      source: 'channel',
    };
    setLoading(true);
    clearRecords();
    getStreamingChannelShare(paramsInfo)
      .then((data: any) => {
        let { list, total, total_old, total_new } = data;
        if (list && list.length > 0) {
          let newList = list
            .map((item: any) => ({
              ...item,
              ration_percent: getRationPercentValue(item.new, item.old),
              value: item.new,
            }))
            ?.sort(sortable.comparer([sort]));
          setTotalColumn({
            user_name: 'Total',
            value: total_new,
            ration_percent: getRationPercentValue(total_new, total_old),
          });
          setTableRecords(newList);
          const pieData = dealwithChannelShareChartData(
            total_new,
            newList || [],
          );
          setPieAndBarRecords(pieData);
          pieAndBarRecordsMap.current = arrayToSimpleObj(
            pieData,
            'user_name',
            'user_id',
          );
        }
        setTotal(total || 0);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    // 重置到饼图上，因为线图需要饼图数据
    setType('pie');
    getServerDataInfo();
  }, [params, metric]);

  const getLineData = () => {
    let paramsInfo = {
      ...params,
      ...gameInfo,
      metric: metric,
      source: 'channel',
      user_id_list: pieAndBarRecords
        .filter((item) => item.name !== 'Others')
        .map((item) => item.user_id),
    };
    setLineLoading(true);
    getStreamingChannelShareTrends(paramsInfo)
      .then((resultData: any) => {
        const dateType = paramsInfo.date_type || 'daily';
        const nameMap: any = pieAndBarRecordsMap.current;
        let { timeline_of_start_time, timeline_of_end_time, list } =
          resultData || {};
        let data = supplementSingleData(
          timeline_of_start_time,
          timeline_of_end_time,
          list?.map((item: any) => ({
            time: item.time,
            name: nameMap[item.key] || item.key,
            count: item.value,
          })),
          dateType,
        );
        const needTime = ['daily', 'weekly'].includes(dateType)
          ? 'days'
          : dateType === 'hourly'
          ? 'hourly'
          : 'year';
        let { newArr, timeArr } = handleMentionData(data, needTime);
        setLineXAxisData(timeArr);
        setLineSeriesData(newArr);
      })
      .finally(() => {
        setLineLoading(false);
      });
  };

  useEffect(() => {
    type === 'line' && getLineData();
  }, [pieAndBarRecords, type]);

  const download = () => {
    downloadLegacy({
      limit: 150000,
      params: {
        ...params,
        ...gameInfo,
        metric,
        sort_item: sort.by,
        sort_model: sort.order,
        source: 'channel',
        page: 1,
        page_size: 1000,
      },
      downloadFunc: 'getStreamingChannelShare',
      sheetName: 'Streamer',
      total,
      simpleName: 'Streaming',
    });
  };

  const operationsMap = useMemo(() => {
    if (loading || pieAndBarRecords.length === 0) return [];

    return [barIcon, lineIcon, pieIcon];
  }, [pieAndBarRecords, loading]);

  const formatDate = formatDateFunc(params.date_type);

  const title = useMemo(() => {
    let { start_time, end_time } = params;
    return uniqueArray([start_time, end_time].map(formatDate)).join(' ~ ');
  }, [params]);

  const chartElementMap = {
    pie: (
      <PieChart
        tooltipFormatterFunc={(_params: any) => {
          const rightTitle = formatMessage({
            id: metricsMap[metric] || NO_DATE.EMPTY,
          });
          const config = formatChartParamsDate({
            title: title,
            rightTitle,
            params: _params,
            valueFormat: formatMetricValue,
            listTitleFunc: (item) => item.name,
          });
          return config;
        }}
        title={gameName}
        formatValue={formatMetricValue}
        height={338}
        seriesData={pieAndBarRecords}
        loading={loading}
      ></PieChart>
    ),
    line: (
      <LineChart
        height={338}
        seriesDataArray={lineSeriesData}
        loading={lineLoading}
        tooltipFormatterFunc={(_params) => {
          const rightTitle = formatMessage({
            id: metricsMap[metric] || NO_DATE.EMPTY,
          });
          let leftTitle = listTimeTitleFunc(
            _params[0]?.axisValue,
            params.date_type,
          );

          const config = formatChartParamsDate({
            title: leftTitle,
            rightTitle,
            params: _params,
            valueFormat: formatMetricValue,
            dealwithRecords(records, total) {
              records.unshift({
                text: formatMessage({ id: 'Sum' }),
                value: formatMetricValue(total),
                originValue: total,
                originText: 'Sum',
              });
            },
          });

          return config;
        }}
        xAxisData={lineXAxisData}
        dateType={params.date_type}
      ></LineChart>
    ),
    bar: (
      <BarChart
        height={338}
        seriesData={pieAndBarRecords}
        loading={loading}
        tooltipFormatterFunc={(_params: any) => {
          const rightTitle = formatMessage({
            id: metricsMap[metric] || NO_DATE.EMPTY,
          });
          const config = formatChartParamsDate({
            title: title,
            rightTitle,
            params: _params,
            listTitleFunc: (item) => item.name,
            valueFormat: formatMetricValue,
          });

          return config;
        }}
      ></BarChart>
    ),
  };

  return (
    <CardCommon
      className="dbo-community-card dbo-channelsshare-card"
      title={
        <BubbleCommon
          tipsMap={showTitleTips ? influencerCommonTipsText : null}
          showIcon={showTitleTips}
        >
          {formatMessage({ id: 'Channels Share' })}
        </BubbleCommon>
      }
      operations={operationsMap}
      // defaultOperation={type}
      operationInfo={[type, setType]}
      hasDownload
      downloadLoading={downloading}
      handleDownloadOnClick={download}
      handleOperationsOnClick={({ symbol }: any) => setType(symbol)}
    >
      <>
        <div style={{ marginBottom: 20 }}>{chartElementMap[type]}</div>
        <ChannelsShareTable
          sort={sort}
          sortChange={setSort}
          total={total}
          metric={metric}
          totalColumn={totalColumn}
          tableList={tableRecords}
          defaultPageSize={10}
          loading={loading}
        ></ChannelsShareTable>
      </>
    </CardCommon>
  );
}
